import { memo, useState, useEffect, useCallback } from 'react'
import type { Program, MediaAsset, MediaVersion, LiveSource } from '../../types/index.js'
import { useProgramActions } from '../../hooks/usePrograms.js'
import { useStore } from '../../store/index.js'
import { liveSourcesApi, type MediaCheckResult } from '../../lib/api.js'
import { ANTENNA_ELEMENT_PROGRAM_TYPES, formatDuration, formatFileSize } from '../../lib/utils.js'
import { MediaBrowserModal } from './MediaBrowserModal.js'
import type { ProbeResult as MediaBrowseResult } from '../../lib/mediaBrowserApi.js'

// ─── Props ────────────────────────────────────────────────────────────────────

interface MediaAssetManagerProps {
  program: Program
  title?: string
  onChanged?: () => void | Promise<void>
  disabled?: boolean
  disabledReason?: string
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const MediaAssetManager = memo(function MediaAssetManager({ program, title, onChanged, disabled = false, disabledReason }: MediaAssetManagerProps) {
  const { checkMedia, deleteMedia, addMedia, updateMedia, updateProgram } = useProgramActions()
  const activeChannelId = useStore(s => s.activeChannelId)
  const assets   = program.mediaAssets ?? []
  // Emplacement visé par le navigateur : fichier unique, ou l'une des versions.
  const [browserTarget, setBrowserTarget] = useState<'MAIN' | MediaVersion | null>(null)
  const [liveSources, setLiveSources] = useState<LiveSource[]>([])
  const [updating,    setUpdating]    = useState(false)
  const [mediaError,  setMediaError]  = useState('')

  // Charge les sources live si c'est un programme direct
  useEffect(() => {
    if (program.isLive && activeChannelId) {
      liveSourcesApi.list(activeChannelId)
        .then(setLiveSources)
        .catch(console.error)
    }
  }, [program.isLive, activeChannelId])

  useEffect(() => {
    if (disabled) setBrowserTarget(null)
  }, [disabled])

  const handleSelectSource = useCallback(async (sourceId: string) => {
    if (updating || disabled) return
    setUpdating(true)
    try {
      await updateProgram(program.id, { liveSourceId: sourceId || null } as any)
    } catch (err) {
      console.error('[select source error]', err)
    } finally {
      setUpdating(false)
    }
  }, [program.id, updateProgram, updating, disabled])

  // Les programmes directs (isLive) n'ont pas de fichier média
  if (program.isLive) {
    return (
      <section className="catalog-media-section">
        <div className="catalog-media-section__header catalog-media-section__header--live">
          <h3 className="catalog-media-section__title">
            {title ?? 'Source live associée'}
          </h3>
          <span className="catalog-status-badge catalog-status-badge--warning">
            Programme direct — pas de fichier requis
          </span>
        </div>

        <div className="catalog-source-grid">
          {/* Liste des sources réelles */}
          {liveSources.map(s => (
            <SourceTile
              key={s.id}
              name={s.name}
              type={s.sourceType}
              active={program.liveSourceId === s.id}
              onClick={() => handleSelectSource(s.id)}
              disabled={updating || disabled}
            />
          ))}
        </div>
        
        {liveSources.length === 0 && (
          <div className="catalog-empty-copy catalog-media-section__empty">
            Aucune source configurée pour cette chaîne.
          </div>
        )}
      </section>
    )
  }

  // Un seul média principal est éditable. Les anciennes associations
  // conservées pour l'historique ne sont pas proposées à l'opérateur.
  const mainAssets  = assets.filter(a => !a.isProxy)
  const primaryAsset = mainAssets[mainAssets.length - 1] ?? null
  // Épisode livré en deux montages : un emplacement par version. Réservé aux
  // fiches en une seule partie — un multipartie a déjà un fichier par partie.
  const canHaveVersions = !program.parentProgramId && !(program.parts?.length)
    && !ANTENNA_ELEMENT_PROGRAM_TYPES.includes(program.type)
  const dualVersion = canHaveVersions && program.dualVersion
  const longAsset  = mainAssets.find(asset => asset.version === 'LONG') ?? null
  const shortAsset = mainAssets.find(asset => asset.version === 'SHORT') ?? null
  // Fichier déjà présent mais pas encore étiqueté : il reste visible, à
  // classer, plutôt que de disparaître au passage en deux versions.
  const unlabeledAsset = dualVersion ? mainAssets.find(asset => !asset.version) ?? null : null

  const handleToggleDual = async () => {
    if (updating || disabled) return
    setUpdating(true)
    setMediaError('')
    try {
      await updateProgram(program.id, { dualVersion: !program.dualVersion })
      await onChanged?.()
    } catch (err) {
      setMediaError(err instanceof Error ? err.message : 'Impossible de modifier l’option')
    } finally {
      setUpdating(false)
    }
  }

  const handleSelectFile = async (result: MediaBrowseResult) => {
    try {
      await addMedia(program.id, {
        filePath:       result.filePath,
        fileName:       result.fileName,
        codec:          result.codec         ?? undefined,
        container:      result.container     ?? undefined,
        durationSec:    result.durationSec   ?? undefined,
        resolution:     result.resolution    ?? undefined,
        fileSizeBytes:  result.fileSizeBytes ?? undefined,
        isProxy:        false,
        // Emplacement choisi : il l'emporte sur le suffixe du nom de fichier.
        ...(browserTarget !== 'MAIN' ? { version: browserTarget } : {}),
      })
      await onChanged?.()
      setMediaError('')
    } catch (err) {
      setMediaError(err instanceof Error ? err.message : 'Impossible de remplacer le média')
    } finally {
      setBrowserTarget(null)
    }
  }

  const handleLabel = async (asset: MediaAsset, version: MediaVersion) => {
    try {
      await updateMedia(asset.id, { version })
      await onChanged?.()
      setMediaError('')
    } catch (err) {
      setMediaError(err instanceof Error ? err.message : 'Impossible de classer le fichier')
    }
  }

  const renderRow = (asset: MediaAsset, isLast: boolean) => (
    <MediaRow
      key={asset.id}
      asset={asset}
      programDuration={program.durationSec}
      isLast={isLast}
      onCheck={() => checkMedia(asset.id)}
      disabled={disabled}
      disabledReason={disabledReason}
      onDelete={async () => {
        await deleteMedia(asset.id, program.id)
        await onChanged?.()
      }}
    />
  )

  return (
    <section className="catalog-media-section">
      <div className="catalog-media-section__header">
        <h3 className="catalog-media-section__title">
          {title ?? (dualVersion ? 'Médias associés' : 'Média associé')}
        </h3>
        {!dualVersion && (
          <button
            type="button"
            onClick={() => { setMediaError(''); setBrowserTarget('MAIN') }}
            className="catalog-media-associate"
            disabled={disabled}
            title={disabled ? disabledReason : undefined}
          >
            {primaryAsset ? 'Remplacer le fichier' : '+ Associer un fichier'}
          </button>
        )}
      </div>

      {canHaveVersions && (
        <label
          className="catalog-media-dual"
          title={dualVersion && longAsset && shortAsset ? 'Supprimez l’une des deux versions pour revenir à un fichier unique.' : undefined}
        >
          <input
            type="checkbox"
            checked={dualVersion}
            onChange={handleToggleDual}
            disabled={disabled || updating || (dualVersion && Boolean(longAsset && shortAsset))}
          />
          <span>
            Deux versions (longue / courte)
            {dualVersion && (
              <span className="catalog-media-dual__hint">
                {longAsset && shortAsset
                  ? 'Le créneau du modèle choisit la version la plus proche de sa durée ; l’ajout manuel propose les deux.'
                  : 'Un fichier nommé …_VL ou …_VC est classé automatiquement.'}
              </span>
            )}
          </span>
        </label>
      )}

      {disabled && disabledReason && (
        <div className="tp-feedback tp-feedback--warning catalog-media-section__feedback">
          {disabledReason}
        </div>
      )}

      {/* Navigateur de médias */}
      {browserTarget && (
        <MediaBrowserModal
          onSelect={handleSelectFile}
          onClose={() => setBrowserTarget(null)}
        />
      )}

      {mediaError && (
        <div className="tp-feedback tp-feedback--error catalog-media-section__feedback">
          {mediaError}
        </div>
      )}

      {dualVersion ? (
        <div className="catalog-media-card">
          {(['LONG', 'SHORT'] as const).map((version, index) => {
            const asset = version === 'LONG' ? longAsset : shortAsset
            const label = version === 'LONG' ? 'Version longue' : 'Version courte'
            return (
              <div key={version} className={`catalog-media-version${index === 0 || unlabeledAsset ? '' : ' is-last'}`}>
                <div className="catalog-media-version__header">
                  <span className="catalog-media-version__label">{label}</span>
                  <button
                    type="button"
                    onClick={() => { setMediaError(''); setBrowserTarget(version) }}
                    className="catalog-media-associate"
                    disabled={disabled}
                    title={disabled ? disabledReason : undefined}
                  >
                    {asset ? 'Remplacer' : '+ Associer'}
                  </button>
                </div>
                {asset
                  ? renderRow(asset, true)
                  : <div className="catalog-media-card__empty">Aucun fichier pour cette version</div>}
              </div>
            )
          })}
          {unlabeledAsset && (
            <div className="catalog-media-version is-last">
              <div className="catalog-media-version__header">
                <span className="catalog-media-version__label">Fichier à classer</span>
                <div className="catalog-media-version__actions">
                  {!longAsset && <MiniBtn label="Longue" onClick={() => handleLabel(unlabeledAsset, 'LONG')} disabled={disabled} />}
                  {!shortAsset && <MiniBtn label="Courte" onClick={() => handleLabel(unlabeledAsset, 'SHORT')} disabled={disabled} />}
                </div>
              </div>
              {renderRow(unlabeledAsset, true)}
            </div>
          )}
        </div>
      ) : (
        /* Un seul fichier principal est affiché et éditable. */
        <div className="catalog-media-card">
          {primaryAsset && renderRow(primaryAsset, true)}
          {!primaryAsset && (
            <div className="catalog-media-card__empty">
              Aucun fichier associé à ce programme
            </div>
          )}
        </div>
      )}
    </section>
  )
})

// ─── Ligne de média ───────────────────────────────────────────────────────────

/**
 * Ton du bandeau de résultat.
 *
 * Une piste corrompue ou muette est aussi bloquante qu'un codec refusé, même si
 * le média reste diffusable au sens du statut ; un simple écart de format ne
 * mérite que l'orange.
 */
function resultTone(result: MediaCheckResult): 'is-success' | 'is-warning' | 'is-danger' {
  if (result.issues.length === 0) return 'is-success'
  const blocking = result.status !== 'OK' || result.audio?.severity === 'ERROR'
  return blocking ? 'is-danger' : 'is-warning'
}

const MediaRow = memo(function MediaRow({
  asset, programDuration, isLast, onCheck, onDelete, disabled, disabledReason,
}: {
  asset:           MediaAsset
  programDuration: number
  isLast:          boolean
  onCheck:         () => Promise<MediaCheckResult>
  onDelete:        () => void | Promise<void>
  disabled?:       boolean
  disabledReason?: string
}) {
  const [checking, setChecking] = useState(false)
  const [checkResult, setCheckResult] = useState<MediaCheckResult | null>(null)

  const handleCheck = async () => {
    setChecking(true)
    try {
      setCheckResult(await onCheck())
    } catch (err) {
      console.error('[check media error]', err)
      setCheckResult({ status: 'ERROR', issues: ['Erreur de vérification'], audio: null, metadata: {} })
    } finally {
      setChecking(false)
    }
  }

  const statusTone = asset.status === 'OK'
    ? 'success'
    : asset.status === 'UNCHECKED'
      ? 'muted'
      : asset.status === 'DURATION_MISMATCH'
        ? 'warning'
        : 'danger'
  // La fiche dure ce que dure la version longue : une version courte n'est pas
  // un écart, c'est son montage.
  const driftSec = asset.durationSec && programDuration && asset.version !== 'SHORT'
    ? asset.durationSec - programDuration
    : null
  const hasDrift = driftSec !== null && Math.abs(driftSec) > 30

  return (
    <div className={isLast ? 'catalog-media-row is-last' : 'catalog-media-row'}>
      <div className="catalog-media-row__layout">

        {/* Icône type */}
        <div className={`catalog-media-row__type catalog-media-row__type--${statusTone}`}>
          {asset.isProxy ? 'PRX' : 'HD'}
        </div>

        {/* Infos fichier */}
        <div className="catalog-media-row__copy">
          <div className="catalog-media-row__name">
            {asset.fileName}
          </div>
          <div className="catalog-media-row__meta">
            {asset.codec     && <span>{asset.codec}</span>}
            {asset.container && <span>· {asset.container}</span>}
            {asset.durationSec && <span>· {formatDuration(asset.durationSec)}</span>}
            {asset.resolution  && <span>· {asset.resolution}</span>}
            {asset.fileSizeBytes && <span>· {formatFileSize(asset.fileSizeBytes)}</span>}
            {hasDrift && (
              <span className="catalog-media-row__drift">
                · Écart {driftSec! > 0 ? '+' : ''}{driftSec}s
              </span>
            )}
          </div>
        </div>

        {/* Badge statut */}
        <span className={`catalog-status-badge catalog-status-badge--${statusTone}`}>
          {asset.status === 'OK' ? 'OK' :
           asset.status === 'MISSING' ? 'Manquant' :
           asset.status === 'INVALID_CODEC' ? 'Codec !' :
           asset.status === 'DURATION_MISMATCH' ? 'Durée ?' : 'Non vérifié'}
        </span>

        {/* Actions */}
        <div className="catalog-media-row__actions">
          <MiniBtn label={checking ? '…' : 'Vérifier'} onClick={handleCheck} disabled={checking || disabled} title={disabled ? disabledReason : undefined} />
          <MiniBtn label="Suppr." onClick={onDelete} disabled={disabled} title={disabled ? disabledReason : undefined} variant="danger" />
        </div>
      </div>

      {/* Résultat de vérification.
          Le ton suit les remontées, pas le seul `status` : un défaut audio ne
          dégrade pas le statut diffusable et passerait sinon inaperçu. */}
      {checkResult && (
        <div className={`catalog-media-row__result ${resultTone(checkResult)}`}>
          {checkResult.issues.length === 0
            ? (checkResult.audio
                ? '✓ Fichier compatible CasparCG — audio décodé sans erreur'
                : '✓ Fichier compatible CasparCG')
            : checkResult.issues.map((issue, i) => <div key={i}>⚠ {issue}</div>)
          }
        </div>
      )}

      {/* Erreur précédente */}
      {asset.lastError && !checkResult && (
        <div className="catalog-media-row__result is-danger is-compact">
          {asset.lastError}
        </div>
      )}
    </div>
  )
})

// ─── Source live (Tuile) ──────────────────────────────────────────────────────

const SourceTile = memo(function SourceTile({
  name, type, active, onClick, disabled, icon,
}: {
  name:      string
  type?:     string
  active:    boolean
  onClick:   () => void
  disabled?: boolean
  icon?:     string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={active ? 'catalog-source-tile is-active' : 'catalog-source-tile'}
    >
      <div className="catalog-source-tile__main">
        <span className="catalog-source-tile__icon">
          {icon || (type === 'DECKLINK' ? '🔌' : type === 'INTERNET' || type === 'RTMP' || type === 'RTSP' || type === 'SRT' ? '📡' : '💻')}
        </span>
        <span className="catalog-source-tile__name">
          {name}
        </span>
      </div>
      {type && (
        <span className="catalog-source-tile__type">
          {type}
        </span>
      )}
    </button>
  )
})

// ─── Mini bouton ──────────────────────────────────────────────────────────────

const MiniBtn = memo(function MiniBtn({
  label, onClick, disabled, title, variant,
}: { label: string; onClick: () => void; disabled?: boolean; title?: string; variant?: 'danger' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`catalog-mini-button${variant === 'danger' ? ' is-danger' : ''}`}
    >
      {label}
    </button>
  )
})
